import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCircle2, UserPlus, Clock, X, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useEventNotificationStore, type EventNotification } from '@/stores/eventNotificationStore'

const ICON_MAP: Record<EventNotification['type'], typeof Bell> = {
  task_created: CheckCircle2,
  task_assigned: UserPlus,
  task_updated: Bell,
  timer_stopped: Clock,
  notification_sent: Bell,
}

const AUTO_DISMISS_MS = 10_000

function ToastItem({
  notification,
  onDismiss,
  onNavigate,
}: {
  notification: EventNotification
  onDismiss: (id: string) => void
  onNavigate: (taskId?: string) => void
}) {
  const Icon = ICON_MAP[notification.type] || Bell

  useEffect(() => {
    const timer = setTimeout(() => onDismiss(notification.id), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [notification.id, onDismiss])

  return (
    <div
      className={cn(
        'animate-in slide-in-from-top-2 duration-300',
        'flex items-start gap-3 rounded-lg glass-surface p-3 shadow-xl',
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{notification.title}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {notification.description}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {notification.taskId && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => onNavigate(notification.taskId)}
          >
            <ArrowRight className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => onDismiss(notification.id)}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}

export function AgentEventToast() {
  const navigate = useNavigate()
  const { notifications, dismiss } = useEventNotificationStore()

  const visible = notifications.filter((n) => !n.dismissed).slice(0, 3)

  const handleNavigate = useCallback(
    (taskId?: string) => {
      if (taskId) {
        navigate('/tasks')
      }
    },
    [navigate],
  )

  if (visible.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 w-80">
      {visible.map((n) => (
        <ToastItem
          key={n.id}
          notification={n}
          onDismiss={dismiss}
          onNavigate={handleNavigate}
        />
      ))}
    </div>
  )
}
